import { createSlice } from "@reduxjs/toolkit"
import isEqual from "lodash.isequal"

import { logout } from "../../LoginPage/redux/auth.actions"
import { preferencesSuccess } from "../../User/redux/user.actions"
import {
  getPricingPreferencesFeature,
  LEGACY_PRICING_PREFERENCES_FEATURE,
  PRICING_PREFERENCES_VERSION,
  PRICING_TAB,
  PRICING_SLICE_NAME as SLICE_NAME
} from "./pricing.constants"
import {
  createLookupPricing,
  createPricing,
  deletePricing,
  getAllPricingSchemes,
  getLookupPricing,
  getLookupPricingAssignmentOptions,
  getLookupPricingFilterOptions,
  getPricing,
  getPricingFilterOptions,
  updateLookupPricing,
  updatePricing
} from "./pricing.thunks"

// Not persisted: each tab's filters come back from the user's saved preferences, and every
// response is re-fetched on mount.
const initialFilters = {
  [PRICING_TAB.ALERTS]: {
    pricing_schema: "all",
    type: "all",
    applied: "all",
    count_of_free_alerts: { min: null, max: null, type: null }
  },
  [PRICING_TAB.LOOKUPS]: {
    pricing_schema: "all",
    free_lookups: { min: null, max: null, type: null },
    applied: "all"
  }
}

const initialState = {
  filters: initialFilters,
  visibleFilters: {
    [PRICING_TAB.ALERTS]: {
      pricing_schema: true,
      type: true,
      count_of_free_alerts: true,
      applied: true
    },
    [PRICING_TAB.LOOKUPS]: { pricing_schema: true, free_lookups: true, applied: true }
  },
  filterOptions: {
    [PRICING_TAB.ALERTS]: { pricing_schema: [], type: [], applied: [] },
    [PRICING_TAB.LOOKUPS]: { pricing_schema: [], applied: [] }
  },

  alertsPricing: { count: 0, results: [] },
  alertsPricingFetching: false,

  lookupPricing: { count: 0, results: [] },
  lookupPricingFetching: false,

  // The create/edit modal is open on one tab at a time, so both tabs share the save state.
  saveFetching: false,

  // Business settings' scheme picker; kept apart so it never overwrites the admin table.
  allSchemes: [],
  allSchemesFetching: false,

  lookupAssignmentOptions: [],
  lookupAssignmentOptionsFetching: false
}

const readTabPreference = (payload, tab) => {
  const preference = payload?.[getPricingPreferencesFeature(tab)]?.data

  if (preference?.version === PRICING_PREFERENCES_VERSION && preference.filters) {
    return { filters: preference.filters, visibleFilters: preference.visibleFilters }
  }

  if (tab !== PRICING_TAB.ALERTS) return null

  // Read until the Alerts tab saves under its own key. The saved `columnsAdmin` is dropped: the
  // Alerts columns are fixed now.
  const legacy = payload?.[LEGACY_PRICING_PREFERENCES_FEATURE]?.data
  if (legacy?.version === PRICING_PREFERENCES_VERSION && legacy.filters) {
    return { filters: legacy.filters, visibleFilters: legacy.visibleFiltersAdmin }
  }

  return null
}

const setSaveFetching = (state) => {
  state.saveFetching = true
}
const clearSaveFetching = (state) => {
  state.saveFetching = false
}

const slice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    changeFilters: (state, { payload: { tab, filters } }) => {
      state.filters[tab] = { ...state.filters[tab], ...filters }
    },
    changeVisibleFilters: (state, { payload: { tab, visibleFilters } }) => {
      state.visibleFilters[tab] = { ...state.visibleFilters[tab], ...visibleFilters }
    },
    resetFilters: (state, { payload: tab }) => {
      state.filters[tab] = initialFilters[tab]
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(logout, () => initialState)

      // Merged over the defaults, so a filter added after the preference was saved still has a value.
      .addCase(preferencesSuccess, (state, { payload }) => {
        Object.values(PRICING_TAB).forEach((tab) => {
          const preference = readTabPreference(payload, tab)
          if (!preference) return

          state.filters[tab] = { ...initialFilters[tab], ...preference.filters }
          state.visibleFilters[tab] = {
            ...initialState.visibleFilters[tab],
            ...preference.visibleFilters
          }
        })
      })

      .addCase(getPricing.pending, (state) => {
        state.alertsPricingFetching = true
      })
      .addCase(getPricing.fulfilled, (state, { payload }) => {
        state.alertsPricingFetching = false
        state.alertsPricing = {
          count: payload?.count ?? payload?.results?.length ?? 0,
          results: payload?.results || []
        }
      })
      .addCase(getPricing.rejected, (state) => {
        state.alertsPricingFetching = false
      })

      .addCase(getPricingFilterOptions.fulfilled, (state, { payload }) => {
        state.filterOptions[PRICING_TAB.ALERTS] = payload
      })

      .addCase(createPricing.pending, setSaveFetching)
      .addCase(createPricing.fulfilled, clearSaveFetching)
      .addCase(createPricing.rejected, clearSaveFetching)

      .addCase(updatePricing.pending, setSaveFetching)
      .addCase(updatePricing.fulfilled, (state, { payload }) => {
        state.saveFetching = false
        state.alertsPricing.results = state.alertsPricing.results.map((price) =>
          price.id === payload.id ? { ...price, ...payload } : price
        )
      })
      .addCase(updatePricing.rejected, clearSaveFetching)

      .addCase(deletePricing.pending, (state) => {
        state.alertsPricingFetching = true
      })
      .addCase(deletePricing.fulfilled, (state, { payload: id }) => {
        state.alertsPricingFetching = false
        state.alertsPricing = {
          count: state.alertsPricing.count - 1,
          results: state.alertsPricing.results.filter((price) => price.id !== id)
        }
      })
      .addCase(deletePricing.rejected, (state) => {
        state.alertsPricingFetching = false
      })

      .addCase(getAllPricingSchemes.pending, (state) => {
        state.allSchemesFetching = true
      })
      .addCase(getAllPricingSchemes.fulfilled, (state, { payload }) => {
        state.allSchemesFetching = false
        state.allSchemes = payload || []
      })
      .addCase(getAllPricingSchemes.rejected, (state) => {
        state.allSchemesFetching = false
      })

      .addCase(getLookupPricing.pending, (state) => {
        state.lookupPricingFetching = true
      })
      .addCase(getLookupPricing.fulfilled, (state, { payload }) => {
        state.lookupPricingFetching = false
        state.lookupPricing = { count: payload?.count || 0, results: payload?.results || [] }
      })
      .addCase(getLookupPricing.rejected, (state) => {
        state.lookupPricingFetching = false
      })

      .addCase(getLookupPricingAssignmentOptions.pending, (state) => {
        state.lookupAssignmentOptionsFetching = true
      })
      .addCase(getLookupPricingAssignmentOptions.fulfilled, (state, { payload }) => {
        state.lookupAssignmentOptionsFetching = false
        state.lookupAssignmentOptions = payload || []
      })
      .addCase(getLookupPricingAssignmentOptions.rejected, (state) => {
        state.lookupAssignmentOptionsFetching = false
      })

      .addCase(getLookupPricingFilterOptions.fulfilled, (state, { payload }) => {
        state.filterOptions[PRICING_TAB.LOOKUPS] = {
          pricing_schema: payload?.name || [],
          applied: payload?.applied || []
        }
      })

      .addCase(createLookupPricing.pending, setSaveFetching)
      .addCase(createLookupPricing.fulfilled, clearSaveFetching)
      .addCase(createLookupPricing.rejected, clearSaveFetching)

      .addCase(updateLookupPricing.pending, setSaveFetching)
      .addCase(updateLookupPricing.fulfilled, (state, { payload }) => {
        state.saveFetching = false
        state.lookupPricing.results = state.lookupPricing.results.map((scheme) =>
          scheme.id === payload.id ? payload : scheme
        )
      })
      .addCase(updateLookupPricing.rejected, clearSaveFetching)
  }
})

export const { changeFilters, changeVisibleFilters, resetFilters } = slice.actions

export const selectPricingFilters = (tab) => (state) => state.pricing.filters[tab]
export const selectPricingVisibleFilters = (tab) => (state) => state.pricing.visibleFilters[tab]
export const selectPricingFilterOptions = (tab) => (state) => state.pricing.filterOptions[tab]
export const selectArePricingFiltersDefault = (tab) => (state) =>
  isEqual(state.pricing.filters[tab], initialFilters[tab])

export const selectAlertsPricing = (state) => state.pricing.alertsPricing
export const selectAlertsPricingFetching = (state) => state.pricing.alertsPricingFetching

export const selectLookupPricing = (state) => state.pricing.lookupPricing
export const selectLookupPricingFetching = (state) => state.pricing.lookupPricingFetching

export const selectPricingSaveFetching = (state) => state.pricing.saveFetching

export const selectAllPricingSchemes = (state) => state.pricing.allSchemes
export const selectAllPricingSchemesFetching = (state) => state.pricing.allSchemesFetching

export const selectLookupAssignmentOptions = (state) => state.pricing.lookupAssignmentOptions
export const selectLookupAssignmentOptionsFetching = (state) =>
  state.pricing.lookupAssignmentOptionsFetching

export default slice.reducer
